import { useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useEffect, useState } from 'react';
import { trackEvent } from '../utils/ga4';
import '../styles/result.style.css';
import logoHeader from '../assets/images/logo-header.png';
import resultTypeIcon from '../assets/images/result-type-mock.png';
import resultTypeStar1 from '../assets/images/star-1.png';
import resultTypeStar2 from '../assets/images/star-2.png';
import resultTypeStar3 from '../assets/images/star-3.png';
import resultTypeStar4 from '../assets/images/star-4.png';
import resultTypeStar5 from '../assets/images/star-5.png';
import resultTypeStar6 from '../assets/images/star-6.png';
import resultCollaborationIcon from '../assets/images/smiley-happy.png';
import resultCollaborationIcon2 from '../assets/images/smiley-neutral.png';
import resultCollaborationMock from '../assets/images/colla-mock.png';
import mascotResult from '../assets/images/mascot-result.png';
import mascotResultShadow from '../assets/images/main-shadow.png';
import unlockIcon from '../assets/images/lock-open.png';
import logo from '../assets/images/logo.png';
import scoutoutLogo from '../assets/images/scoutout-logo.png';
import mascotUnlock from '../assets/images/mascot-unlock.png';
import unlock1 from '../assets/images/unlock-1.png';
import unlock2 from '../assets/images/unlock-2.png';
import unlock3 from '../assets/images/unlock-3.png';
import unlock4 from '../assets/images/unlock-4.png';

// Import persona type images (colored)
import dragonIcon from '../assets/images/types/dragon.png';
import eagleIcon from '../assets/images/types/eagle.png';
import beeIcon from '../assets/images/types/bee.png';
import octopusIcon from '../assets/images/types/octopus.png';
import wolfIcon from '../assets/images/types/wolf.png';
import catIcon from '../assets/images/types/cat.png';
import horseIcon from '../assets/images/types/horse.png';
import antIcon from '../assets/images/types/ant.png';
import elephantIcon from '../assets/images/types/elephant.png';
import dolphinIcon from '../assets/images/types/dolphin.png';
import monkeyIcon from '../assets/images/types/monkey.png';
import goatIcon from '../assets/images/types/goat.png';
import tigerIcon from '../assets/images/types/tiger.png';
import snakeIcon from '../assets/images/types/snake.png';
import oxIcon from '../assets/images/types/ox.png';
import rabbitIcon from '../assets/images/types/rabbit.png';

// Import persona type images (white)
import dragonIconWhite from '../assets/images/types/white/dragon.png';
import eagleIconWhite from '../assets/images/types/white/eagle.png';
import beeIconWhite from '../assets/images/types/white/bee.png';
import octopusIconWhite from '../assets/images/types/white/octopus.png';
import wolfIconWhite from '../assets/images/types/white/wolf.png';
import catIconWhite from '../assets/images/types/white/cat.png';
import horseIconWhite from '../assets/images/types/white/horse.png';
import antIconWhite from '../assets/images/types/white/ant.png';
import elephantIconWhite from '../assets/images/types/white/elephant.png';
import dolphinIconWhite from '../assets/images/types/white/dolphin.png';
import monkeyIconWhite from '../assets/images/types/white/monkey.png';
import goatIconWhite from '../assets/images/types/white/goat.png';
import tigerIconWhite from '../assets/images/types/white/tiger.png';
import snakeIconWhite from '../assets/images/types/white/snake.png';
import oxIconWhite from '../assets/images/types/white/ox.png';
import rabbitIconWhite from '../assets/images/types/white/rabbit.png';

// Import report data model
import type { ReportData, Persona } from '../models/report-data.model';
import {
    Chart as ChartJS,
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';

ChartJS.register(
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend
);


interface ApiReportResponse {
    reportId: string;
    documentId: string;
    reportContent: ReportData;
    userInfo: {
        email: string;
        submittedAt: string;
        timeElapsed: number;
        timeFormatted: string;
    };
    generatedAt: {
        _seconds: number;
        _nanoseconds: number;
    };
    createdAt: {
        _seconds: number;
        _nanoseconds: number;
    };
    status: string;
    model: string;
    promptVersion: string;
}

const ReportPage = () => {
    const { t } = useLanguage();
    const { reportId } = useParams<{ reportId: string }>();
    const [showModal, setShowModal] = useState(false);
    const [email, setEmail] = useState('');
    const [reportData, setReportData] = useState<ReportData | null>(null);
    const [loading, setLoading] = useState(true);
    const [documentId, setDocumentId] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [regenerating, setRegenerating] = useState(false);
    const [regenerateError, setRegenerateError] = useState<string | null>(null);
    
    // Helper function to get persona icon (colored)
    const getPersonaIcon = (personaName: string) => {
        const iconMap: { [key: string]: string } = {
            'Dragon': dragonIcon,
            'Eagle': eagleIcon,
            'Bee': beeIcon,
            'Octopus': octopusIcon,
            'Wolf': wolfIcon,
            'Cat': catIcon,
            'Horse': horseIcon,
            'Ant': antIcon,
            'Elephant': elephantIcon,
            'Dolphin': dolphinIcon,
            'Monkey': monkeyIcon,
            'Goat': goatIcon,
            'Tiger': tigerIcon,
            'Snake': snakeIcon,
            'Ox': oxIcon,
            'Rabbit': rabbitIcon
        };
        return iconMap[personaName] || resultTypeIcon;
    };

    // Helper function to get persona icon (white)
    const getPersonaIconWhite = (personaName: string) => {
        const iconMap: { [key: string]: string } = {
            'Dragon': dragonIconWhite,
            'Eagle': eagleIconWhite,
            'Bee': beeIconWhite,
            'Octopus': octopusIconWhite,
            'Wolf': wolfIconWhite,
            'Cat': catIconWhite,
            'Horse': horseIconWhite,
            'Ant': antIconWhite,
            'Elephant': elephantIconWhite,
            'Dolphin': dolphinIconWhite,
            'Monkey': monkeyIconWhite,
            'Goat': goatIconWhite,
            'Tiger': tigerIconWhite,
            'Snake': snakeIconWhite,
            'Ox': oxIconWhite,
            'Rabbit': rabbitIconWhite
        };
        return iconMap[personaName] || resultTypeIcon;
    };

    // Helper function to get persona by mindset and capability
    const getPersonaByMindsetCapability = (mindset: string, capability: string) => {
        const personaMap: { [key: string]: { [key: string]: string } } = {
            'Skeptic': {
                'Basic': 'Rabbit',
                'Functional': 'Ox',
                'Proficient': 'Snake',
                'Expert': 'Tiger'
            },
            'Curious': {
                'Basic': 'Goat',
                'Functional': 'Monkey',
                'Proficient': 'Dolphin',
                'Expert': 'Elephant'
            },
            'Adopter': {
                'Basic': 'Ant',
                'Functional': 'Horse',
                'Proficient': 'Cat',
                'Expert': 'Wolf'
            },
            'Pioneer': {
                'Basic': 'Bee',
                'Functional': 'Octopus',
                'Proficient': 'Eagle',
                'Expert': 'Dragon'
            }
        };
        return personaMap[mindset]?.[capability] || '';
    };

    // Helper function to get persona description
    const getPersonaDescription = (persona: Persona) => {
        const descriptions: { [key: string]: string } = {
            'Dragon': 'Visionary Leader',
            'Eagle': 'Strategic Innovator',
            'Bee': 'Collaborative Builder',
            'Octopus': 'Adaptive Connector',
            'Wolf': 'Practical Implementer',
            'Cat': 'Independent Explorer',
            'Horse': 'Reliable Executor',
            'Ant': 'Methodical Worker',
            'Elephant': 'Wise Strategist',
            'Dolphin': 'Creative Collaborator',
            'Monkey': 'Curious Experimenter',
            'Goat': 'Steady Climber',
            'Tiger': 'Bold Transformer',
            'Snake': 'Patient Observer',
            'Ox': 'Steady Foundation',
            'Rabbit': 'Cautious Explorer'
        };
        return descriptions[persona.name.replace('The ', '')] || 'AI Ready Professional';
    };

    // Fetch report data by reportId
    useEffect(() => {
        const fetchReport = async () => {
            if (!reportId) {
                setError('Report ID is required');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const response = await fetch(import.meta.env.VITE_API_URL + `/api/report/${reportId}`);
                const dataRes = await response.json();
                const data = dataRes.data as ApiReportResponse;
                if (!response.ok) {
                    throw new Error('Failed to fetch report');
                }

                if(data.documentId) {
                    setDocumentId(data.documentId);
                }

                if (data.reportContent) {
                    setReportData(data.reportContent);
                } else {
                    throw new Error('Report content not found');
                }
            } catch (err) {
                console.error('Error fetching report:', err);
                setError(err instanceof Error ? err.message : 'Failed to fetch report');
            } finally {
                setLoading(false);
            }
        };

        fetchReport();
    }, [reportId]);

    // Function to regenerate report
    const handleRegenerateReport = async () => {
        if (!reportData) {
            setRegenerateError('No report data available');
            return;
        }

        try {
            setRegenerating(true);
            setRegenerateError(null);
            console.log('🔄 Regenerating report with docId:', documentId || reportId);
            const response = await fetch(import.meta.env.VITE_API_URL + '/api/report/regenerate-by-docid', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    docId: documentId || reportId // Use documentId if available, fallback to reportId
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to regenerate report');
            }

            if (result.success && result.reportData) {
                // Update the report data with regenerated content
                setReportData(result.reportData);
                console.log('✅ Report regenerated successfully:', result);
                
                // Show success message (you can add a toast notification here)
                alert('Report regenerated successfully!');
            } else {
                throw new Error('Invalid response from server');
            }

        } catch (err) {
            console.error('❌ Error regenerating report:', err);
            setRegenerateError(err instanceof Error ? err.message : 'Failed to regenerate report');
            alert(`Failed to regenerate report: ${err instanceof Error ? err.message : 'Unknown error'}`);
        } finally {
            setRegenerating(false);
        }
    };

    console.log('🚀 Report data:', reportData);

    // Radar chart data configuration - two datasets: Openness and Capability
    const radarData = reportData ? {
        labels: reportData.primalPillars.map(pillar => pillar.pillar.charAt(0)),
        datasets: [
            {
                label: 'Openness',
                data: reportData.primalPillars.map(pillar => pillar.openness),
                borderColor: '#60E060',
                backgroundColor: 'rgba(96, 224, 96, 0.2)',
                borderWidth: 2,
                pointBackgroundColor: '#60E060',
                pointBorderColor: '#60E060',
                pointRadius: 5,
                pointHoverRadius: 6,
                fill: true,
            },
            {
                label: 'Capability',
                borderColor: '#FF6B35',
                backgroundColor: 'rgba(255, 107, 53, 0.2)',
                borderWidth: 2,
                pointBackgroundColor: '#FF6B35',
                pointBorderColor: '#FF6B35',
                pointRadius: 5,
                pointHoverRadius: 6,
                fill: false,
            },
        ]
    } : {
        labels: ['P', 'R', 'I', 'M', 'A', 'L'],
        datasets: [
            {
                label: 'Openness',
                data: [3, 3, 3, 3, 3, 3],
                borderColor: '#60E060',
                backgroundColor: 'rgba(96, 224, 96, 0.2)',
                borderWidth: 2,
                pointBackgroundColor: '#60E060',
                pointBorderColor: '#60E060',
                pointRadius: 5,
                pointHoverRadius: 6,
                fill: true,
            },
            {
                label: 'Capability',
                data: [2, 2, 2, 2, 2, 2],
                borderColor: '#FF6B35',
                backgroundColor: 'rgba(255, 107, 53, 0.2)',
                borderWidth: 2,
                pointBackgroundColor: '#FF6B35',
                pointBorderColor: '#FF6B35',
                pointRadius: 5,
                pointHoverRadius: 6,
                fill: false,
                
            },
        ]
    };

    const radarOptions = {
        responsive: true,
        // Chart dimensions
   
        // Chart border and background
        borderWidth: 0,
        interaction: {
            intersect: false
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: false, // Disable tooltips to match reference style
            },
        },
        scales: {
            r: {
                beginAtZero: true,
                max: 5,
                min: 0,
                stepSize: 1,
                ticks: {
                    display: false, // Hide tick labels to match reference
                    stepSize: 1,
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.2)',
                    lineWidth: 1,
                    circular: false,
                },
                angleLines: {
                    lineWidth: 1,
                },
                pointLabels: {
                    display: true,
                    color: reportData ? 
                        reportData.primalPillars.map(pillar => {
                            const label = pillar.pillar.charAt(0);
                            return label === 'R' || label === 'A' || label === 'L' ? '#ffb040' : '#60E060';
                        }) : 
                        ['#60E060', '#ffb040', '#60E060', '#60E060', '#ffb040', '#ffb040'],
                    font: {
                        family: 'LINESeedSansTH, sans-serif',
                        size: 20,
                        weight: '500' as const,
                    },
                    padding: 0,
                },
            },
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className='container'>
                <div className='container-fluid-result'>
                    <div className='result-page'>
                        <div className='result-header'>
                            <div className='result-header-left'>
                                <img src={logoHeader} alt='logo' className='icon' />
                            </div>
                            <div className='result-header-center'>
                                <h1 className='font-bold'>Loading Report...</h1>
                            </div>
                        </div>
                        <div className='result-container'>
                            <div style={{ textAlign: 'center', padding: '50px', color: '#fff' }}>
                                <h2>Loading your AI Readiness Assessment report...</h2>
                                <p>Please wait while we fetch your results.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className='container'>
                <div className='container-fluid-result'>
                    <div className='result-page'>
                        <div className='result-header'>
                            <div className='result-header-left'>
                                <img src={logoHeader} alt='logo' className='icon' />
                            </div>
                            <div className='result-header-center'>
                                <h1 className='font-bold'>Report Not Found</h1>
                            </div>
                        </div>
                        <div className='result-container'>
                            <div style={{ textAlign: 'center', padding: '50px', color: '#fff' }}>
                                <h2>Oops! Report not found</h2>
                                <p>{error}</p>
                                <p>Please check the report ID and try again.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='container'>
            <div className='container-fluid-result'>
                <div className='result-page'>
                    <div className='result-header'>
                        <div className='result-header-left'>
                            <img src={logoHeader} alt='logo' className='icon' />
                        </div>
                        <div className='result-header-center'>
                            <h1 className='font-bold'>{t('result.title')}</h1>
                        </div>
                        {/* <div className='result-header-right'>
                            <button 
                                className='regenerate-button'
                                onClick={handleRegenerateReport}
                                disabled={regenerating || !reportData}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: regenerating ? '#666' : '#4A25E1',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: regenerating ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    fontFamily: 'LINESeedSansTH, sans-serif',
                                    opacity: regenerating ? 0.6 : 1,
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                {regenerating ? 'Regenerating...' : '🔄 Regenerate Report'}
                            </button>
                        </div> */}
                    </div>
                    
                    {/* Regenerate Error Display */}
                    {regenerateError && (
                        <div style={{
                            backgroundColor: '#ffebee',
                            color: '#c62828',
                            padding: '12px 20px',
                            margin: '10px 20px',
                            borderRadius: '8px',
                            border: '1px solid #ffcdd2',
                            fontSize: '14px',
                            fontFamily: 'LINESeedSansTH, sans-serif'
                        }}>
                            <strong>⚠️ Regenerate Error:</strong> {regenerateError}
                        </div>
                    )}
                    
                    <div className='result-container'>
                        <div className='result-type-control'>
                            <div className='result-type-icon-control'>
                                <img 
                                    src={reportData ? getPersonaIcon(reportData.persona.name.replace('The ', '')) : resultTypeIcon} 
                                    alt='result-type-icon' 
                                    className='result-type-icon' 
                                />
                                <img src={resultTypeStar1} alt='result-type-star-1' className='result-type-star-1' />
                                <img src={resultTypeStar2} alt='result-type-star-2' className='result-type-star-2' />
                                <img src={resultTypeStar3} alt='result-type-star-3' className='result-type-star-3' />
                                <img src={resultTypeStar4} alt='result-type-star-4' className='result-type-star-4' />
                                <img src={resultTypeStar5} alt='result-type-star-5' className='result-type-star-5' />
                                <img src={resultTypeStar6} alt='result-type-star-6' className='result-type-star-6' />
                            </div>
                            <span className='result-type-header'>
                                {reportData ? reportData.persona.name : 'The Dragon'}
                            </span>
                            <span className='result-type-sub-header'>
                                {reportData ? getPersonaDescription(reportData.persona) : 'Visionary Leader'}
                            </span>
                            <span className='result-type-description'>
                                {reportData ? reportData.summary : 'you embody AI transformation through visionary architecture. You design ecosystems that redefine competitive advantage and synthesize technologies into platforms, reimagining possibilities around AI\'s potential.'}
                            </span>
                        </div>
                        <div className='result-collaboration-control'>
                            <div className='result-collaboration-item'>
                                <div className='result-collaboration-item-header'>
                                    <img src={resultCollaborationIcon} alt='result-collaboration-icon'
                                        className='result-collaboration-item-icon' />
                                    <span className='result-collaboration-item-header-text'>Easiest Collaboration</span>
                                </div>
                                <div className='result-collaboration-item-description'>
                                    {reportData ? (
                                        <img 
                                            src={getPersonaIconWhite(reportData.collaboration.easiest.replace('The ', ''))} 
                                            alt={reportData.collaboration.easiest}
                                            className='result-collaboration-item-description-text-img' 
                                        />
                                    ) : (
                                        <img src={resultCollaborationMock} alt='result-collaboration-mock'
                                            className='result-collaboration-item-description-text-img' />
                                    )}
                                    <div className='result-collaboration-item-description-text-control'>
                                        <span className='result-collaboration-item-description-text-bold'>
                                            {reportData ? reportData.collaboration.easiest : 'The Elephant'}
                                        </span>
                                        <span className='result-collaboration-item-description-text'>
                                            {reportData ? 'provides complementary strengths for effective collaboration' : 'provides strategic wisdom that grounds your transformative vision'}
                                        </span>
                                    </div>
                                </div>

                                <div className='result-collaboration-item-header'>
                                    <img src={resultCollaborationIcon2} alt='result-collaboration-icon'
                                        className='result-collaboration-item-icon' />
                                    <span className='result-collaboration-item-header-text'>Hardest Collaboration</span>
                                </div>
                                <div className='result-collaboration-item-description'>
                                    {reportData ? (
                                        <img 
                                            src={getPersonaIconWhite(reportData.collaboration.hardest.replace('The ', ''))} 
                                            alt={reportData.collaboration.hardest}
                                            className='result-collaboration-item-description-text-img' 
                                        />
                                    ) : (
                                        <img src={resultCollaborationMock} alt='result-collaboration-mock'
                                            className='result-collaboration-item-description-text-img' />
                                    )}
                                    <div className='result-collaboration-item-description-text-control'>
                                        <span className='result-collaboration-item-description-text-bold'>
                                            {reportData ? reportData.collaboration.hardest : 'The Rabbit'}
                                        </span>
                                        <span className='result-collaboration-item-description-text'>
                                            {reportData ? 'may require different approaches and communication styles' : 'may have different risk tolerance and decision-making approaches'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='result-matrix-control'>
                            <span className='result-matrix-header'>AI Capability vs. Mindset Matrix</span>
                            <div className='result-matrix-grid-control'>
                                <div className='matrix-grid'>
                                    {/* Row 1 - Headers */}
                                    <div className='matrix-cell matrix-combo-header'>MINDSET / CAPABILITY</div>
                                    <div className='matrix-cell matrix-header matrix-mindset'>SKEPTIC</div>
                                    <div className='matrix-cell matrix-header matrix-mindset'>CURIOUS</div>
                                    <div className='matrix-cell matrix-header matrix-mindset'>ADOPTER</div>
                                    <div className='matrix-cell matrix-header matrix-mindset'>PIONEER</div>

                                    {/* Row 2 - EXPERT */}
                                    <div className='matrix-cell matrix-header matrix-capability'>EXPERT</div>
                                    {['Skeptic', 'Curious', 'Adopter', 'Pioneer'].map((mindset) => {
                                        const personaName = getPersonaByMindsetCapability(mindset, 'Expert');
                                        const isCurrentPersona = reportData?.persona.name.replace('The ', '') === personaName;
                                        return (
                                            <div key={`expert-${mindset}`} className={`matrix-cell matrix-icon ${isCurrentPersona ? 'matrix-current-persona' : ''}`}>
                                                <img 
                                                    src={isCurrentPersona ? getPersonaIcon(personaName) : getPersonaIconWhite(personaName)} 
                                                    alt={`The ${personaName}`} 
                                                    className='matrix-persona-icon' 
                                                    title={`The ${personaName}`}
                                                />
                                            </div>
                                        );
                                    })}

                                    {/* Row 3 - PROFICIENT */}
                                    <div className='matrix-cell matrix-header matrix-capability'>PROFICIENT</div>
                                    {['Skeptic', 'Curious', 'Adopter', 'Pioneer'].map((mindset) => {
                                        const personaName = getPersonaByMindsetCapability(mindset, 'Proficient');
                                        const isCurrentPersona = reportData?.persona.name.replace('The ', '') === personaName;
                                        return (
                                            <div key={`proficient-${mindset}`} className={`matrix-cell matrix-icon ${isCurrentPersona ? 'matrix-current-persona' : ''}`}>
                                                <img 
                                                    src={isCurrentPersona ? getPersonaIcon(personaName) : getPersonaIconWhite(personaName)} 
                                                    alt={`The ${personaName}`} 
                                                    className='matrix-persona-icon' 
                                                    title={`The ${personaName}`}
                                                />
                                            </div>
                                        );
                                    })}

                                    {/* Row 4 - FUNCTIONAL */}
                                    <div className='matrix-cell matrix-header matrix-capability'>FUNCTIONAL</div>
                                    {['Skeptic', 'Curious', 'Adopter', 'Pioneer'].map((mindset) => {
                                        const personaName = getPersonaByMindsetCapability(mindset, 'Functional');
                                        const isCurrentPersona = reportData?.persona.name.replace('The ', '') === personaName;
                                        return (
                                            <div key={`functional-${mindset}`} className={`matrix-cell matrix-icon ${isCurrentPersona ? 'matrix-current-persona' : ''}`}>
                                                <img 
                                                    src={isCurrentPersona ? getPersonaIcon(personaName) : getPersonaIconWhite(personaName)} 
                                                    alt={`The ${personaName}`} 
                                                    className='matrix-persona-icon' 
                                                    title={`The ${personaName}`}
                                                />
                                            </div>
                                        );
                                    })}

                                    {/* Row 5 - BASIC */}
                                    <div className='matrix-cell matrix-header matrix-capability'>BASIC</div>
                                    {['Skeptic', 'Curious', 'Adopter', 'Pioneer'].map((mindset) => {
                                        const personaName = getPersonaByMindsetCapability(mindset, 'Basic');
                                        const isCurrentPersona = reportData?.persona.name.replace('The ', '') === personaName;
                                        return (
                                            <div key={`basic-${mindset}`} className={`matrix-cell matrix-icon ${isCurrentPersona ? 'matrix-current-persona' : ''}`}>
                                                <img 
                                                    src={isCurrentPersona ? getPersonaIcon(personaName) : getPersonaIconWhite(personaName)} 
                                                    alt={`The ${personaName}`} 
                                                    className='matrix-persona-icon' 
                                                    title={`The ${personaName}`}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className='result-analysis-control'>
                            <span className='result-analysis-header'>SIX 'PRIMAL' PILLARS ANALYSIS</span>
                            <span className='result-analysis-description'>
                                {reportData ? 
                                    `Your ${reportData.persona.mindset.toLowerCase()} mindset and ${reportData.persona.capabilityLevel.toLowerCase()} capabilities create a foundation for AI readiness, with opportunities for growth and development.` :
                                    'Your pioneer mindset and expertise create a force for transformation, needing only alignment to unleash potential.'
                                }
                            </span>

                            <div className='result-analysis-graph-control'>
                                <div className='radar-chart-container'>
                                    <Radar data={radarData} options={radarOptions} />
                                    {/* <div className='custom-radar-labels'>
                                        {reportData ? 
                                            reportData.primalPillars.map((pillar, index) => {
                                                const label = pillar.pillar.charAt(0);
                                                const color = label === 'R' || label === 'A' || label === 'L' ? '#ffb040' : '#60E060';
                                                const angle = (index * 60) - 90; // 60 degrees per point, starting from top
                                                const radius = 160; // Distance from center - moved outside border
                                                const x = Math.cos(angle * Math.PI / 180) * radius;
                                                const y = Math.sin(angle * Math.PI / 180) * radius;
                                                
                                                return (
                                                    <div
                                                        key={pillar.pillar}
                                                        className='custom-label'
                                                        style={{
                                                            position: 'absolute',
                                                            left: `calc(50% + ${x}px)`,
                                                            top: `calc(50% + ${y}px)`,
                                                            color: color,
                                                            fontSize: '20px',
                                                            fontWeight: 'bold',
                                                            fontFamily: 'LINESeedSansTH, sans-serif',
                                                            pointerEvents: 'none',
                                                            zIndex: 10
                                                        }}
                                                    >
                                                        {label}
                                                    </div>
                                                );
                                            }) :
                                            ['P', 'R', 'I', 'M', 'A', 'L'].map((label, index) => {
                                                const color = label === 'R' || label === 'A' || label === 'L' ? '#ffb040' : '#60E060';
                                                const angle = (index * 60) - 90;
                                                const radius = 120;
                                                const x = Math.cos(angle * Math.PI / 180) * radius;
                                                const y = Math.sin(angle * Math.PI / 180) * radius;
                                                
                                                return (
                                                    <div
                                                        key={label}
                                                        className='custom-label'
                                                        style={{
                                                            position: 'absolute',
                                                            left: `calc(50% + ${x}px)`,
                                                            top: `calc(50% + ${y}px)`,
                                                            transform: 'translate(-50%, -50%)',
                                                            color: color,
                                                            fontSize: '20px',
                                                            fontWeight: 'bold',
                                                            fontFamily: 'LINESeedSansTH, sans-serif',
                                                            pointerEvents: 'none',
                                                            zIndex: 10
                                                        }}
                                                    >
                                                        {label}
                                                    </div>
                                                );
                                            })
                                        }
                                    </div> */}
                                </div>
                            </div>
                            <div className='result-analysis-graph-item-control'>
                                {reportData?.primalPillars ? 
                                    reportData.primalPillars.map((pillar, index) => {
                                        const pillarColors = ['#60E060', '#FFB040', '#60E060', '#60E060', '#FEAB31', '#FEAB31'];
                                        const color = pillarColors[index] || '#60E060';
                                        
                                        return (
                                            <div key={pillar.pillar} className={`pillar-info-box ${pillar.pillar.toLowerCase().replace(' ', '-')}`}>
                                                <div className='pillar-icon' style={{ backgroundColor: color }}>
                                                    <span className='pillar-letter'>{pillar.pillar.charAt(0)}</span>
                                                </div>
                                                <div className='pillar-content'>
                                                    <h3 className='pillar-title' style={{ color: color }}>
                                                        {pillar.pillar} - {pillar.description}
                                                    </h3>
                                                   
                                                    <div className='pillar-descriptions'>
                                                        <div className='pillar-description-item'>
                                                           {pillar.personalize_insight}
                                                        </div>
                                                       
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }) :
                                    // Fallback static pillars
                                    <>
                                        <div className='pillar-info-box proactivation'>
                                            <div className='pillar-icon'>
                                                <span className='pillar-letter'>P</span>
                                            </div>
                                            <div className='pillar-content'>
                                                <h3 className='pillar-title'>Proactivation - Initiative & Curiosity</h3>
                                                <div className='pillar-descriptions'>
                                                    <div className='pillar-description-item'>Evaluates AI outputs for accuracy, fairness, and privacy. Applies governance frameworks and balances innovation. "Let me verify this first."</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='pillar-info-box recognition'>
                                            <div className='pillar-icon' style={{ backgroundColor: '#FFB040' }}>
                                                <span className='pillar-letter'>R</span>
                                            </div>
                                            <div className='pillar-content'>
                                                <h3 className='pillar-title' style={{ color: '#FFB040' }}>Recognition - Conceptual Understanding</h3>
                                                <div className='pillar-descriptions'>
                                                    <div className='pillar-description-item'>Evaluates AI outputs for accuracy, fairness, and privacy. Applies governance frameworks and balances innovation. "Let me verify this first."</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='pillar-info-box integration'>
                                            <div className='pillar-icon'>
                                                <span className='pillar-letter'>I</span>
                                            </div>
                                            <div className='pillar-content'>
                                                <h3 className='pillar-title'>Integration - Collaboration & Synergy</h3>
                                                <div className='pillar-descriptions'>
                                                    <div className='pillar-description-item'>Evaluates AI outputs for accuracy, fairness, and privacy. Applies governance frameworks and balances innovation. "Let me verify this first."</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='pillar-info-box momentum'>
                                            <div className='pillar-icon'>
                                                <span className='pillar-letter'>M</span>
                                            </div>
                                            <div className='pillar-content'>
                                                <h3 className='pillar-title'>Momentum - Learning & Adaptability</h3>
                                                <div className='pillar-descriptions'>
                                                    <div className='pillar-description-item'>Evaluates AI outputs for accuracy, fairness, and privacy. Applies governance frameworks and balances innovation. "Let me verify this first."</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='pillar-info-box application'>
                                            <div className='pillar-icon' style={{ backgroundColor: '#FEAB31' }}>
                                                <span className='pillar-letter'>A</span>
                                            </div>
                                            <div className='pillar-content'>
                                                <h3 className='pillar-title' style={{ color: '#FEAB31' }}>Application - Practical Execution</h3>
                                                <div className='pillar-descriptions'>
                                                    <div className='pillar-description-item'>Evaluates AI outputs for accuracy, fairness, and privacy. Applies governance frameworks and balances innovation. "Let me verify this first."</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='pillar-info-box leadership'>
                                            <div className='pillar-icon' style={{ backgroundColor: '#FEAB31' }}>
                                                <span className='pillar-letter'>L</span>
                                            </div>
                                            <div className='pillar-content'>
                                                <h3 className='pillar-title' style={{ color: '#FEAB31' }}>Leadership - Vision & Governance</h3>
                                                <div className='pillar-descriptions'>
                                                    <div className='pillar-description-item'>Evaluates AI outputs for accuracy, fairness, and privacy. Applies governance frameworks and balances innovation. "Let me verify this first."</div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                }
                            </div>
                        </div>

                        <div className='result-mascot-control'>
                            <div className='result-mascot-img-control'>
                                <img src={mascotResult} alt='mascotResult' className='result-mascot-img' />
                                <img src={mascotResultShadow} alt='mascotResultShadow' className='result-mascot-img-shadow' />
                            </div>
                            <div className='result-mascot-bubble-control'>
                                <span className='result-mascot-bubble-text'>Your results are just</span>
                                <span className='result-mascot-bubble-text-bold'>THE BEGINNING</span>
                            </div>
                        </div>

                        <div className='result-unlock'>
                            <div className='result-unlock-control-img'>
                                <img
                                    src={unlock1}
                                    className='result-unlock-control-img-item'
                                ></img>
                                <img
                                    src={unlock2}
                                    className='result-unlock-control-img-item'
                                ></img>
                                <img
                                    src={unlock3}
                                    className='result-unlock-control-img-item'
                                ></img>
                                <img
                                    src={unlock4}
                                    className='result-unlock-control-img-item'
                                ></img>
                            </div>
                            <div className='result-unlock-control'>
                                <span className='result-unlock-header-text'>
                                    Upgrade now to unlock the full report with in-depth analysis
                                    and insights
                                </span>
                                <a 
                                    href='mailto:team@scoutout.net?subject=Inquiry%20About%20AI%20Readiness%20Report' 
                                    className='result-unlock-button'
                                    onClick={() => {
                                      trackEvent('contact_team_button_clicked', {
                                        button_location: 'report_page_unlock_section',
                                        persona: reportData?.persona.name || 'unknown'
                                      });
                                    }}
                                >
                                    <img
                                        src={unlockIcon}
                                        alt='unlockIcon'
                                        className='result-unlock-button-icon'
                                    />
                                    Contact ScoutOut Team
                                </a>
                            </div>
                        </div>

                        <div className='result-bottom-control'>
                        <img src={logo} alt='logo' className='logo-white' />
                        <h1 className='head-title-process-result' dangerouslySetInnerHTML={{ __html: t('home.title') }}></h1>
                        <h1 className='head-title-process-result-2' dangerouslySetInnerHTML={{ __html: t('home.title2') }}></h1>
                        <div className='powered-by-text-process-result'><span className='powered-by-text-process-result-text'>{t('home.poweredBy')}</span>
                            <img src={scoutoutLogo} alt='logo' className='powered-by-text-process-result-logo' /> Interview AI
                        </div>

                    </div>
                </div>
            </div>
        </div>

            {/* Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <img src={mascotUnlock} alt='mascotUnlock' className='mascot-unlock' />
                        <button className="modal-close-button" onClick={() => setShowModal(false)}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                        <h2 className="modal-header-text">Stay tuned!</h2>
                        <p className="modal-description-text">Full AI readiness Assessment is coming your way soon</p>
                        <input 
                            type="email" 
                            className="modal-input" 
                            placeholder="Your Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        <button className="modal-action-button">Notify Me When Ready</button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ReportPage;
